import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Send,
  Phone,
  Mail,
  CheckCircle,
  Clock,
  Euro,
  Car,
  Calendar,
  User,
  Target,
  CreditCard,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Contact info
    name: "",
    email: "",
    phone: "",
    // Project info
    budget: "",
    vehicle_type: "",
    origin: "",
    pack_interest: "",
    message: "",
    // Qualification questions
    timeline: "",
    budget_confirmed: false,
    financing_type: "",
    vehicle_identified: false,
    decision_maker: false,
    funds_available: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // SEO title
  useEffect(() => {
    document.title = "Contact | Fast&Car - Demandez votre devis gratuit";
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Determine project type based on origin
      let project_type = "unknown";
      if (formData.origin === "Europe") project_type = "europe";
      if (formData.origin === "Corée du Sud") project_type = "korea";

      await axios.post(`${API}/leads`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        source: "contact",
        project_type: project_type,
        budget: formData.budget,
        vehicle_type: formData.vehicle_type,
        pack_interest: formData.pack_interest,
        message: formData.message,
        timeline: formData.timeline,
        budget_confirmed: formData.budget_confirmed,
        financing_type: formData.financing_type,
        vehicle_identified: formData.vehicle_identified,
        decision_maker: formData.decision_maker,
      });

      setIsSubmitted(true);
      toast.success("Votre demande a été envoyée avec succès !");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToStep = (newStep) => {
    // Validate current step before proceeding
    if (newStep > step) {
      if (step === 1) {
        if (!formData.name || !formData.email || !formData.phone) {
          toast.error("Veuillez remplir tous les champs obligatoires");
          return;
        }
      }
      if (step === 2) {
        if (!formData.timeline || !formData.financing_type) {
          toast.error("Veuillez répondre à toutes les questions");
          return;
        }
      }
    }
    setStep(newStep);
  };

  if (isSubmitted) {
    return (
      <div data-testid="contact-page" className="pt-40 min-h-screen">
        <section className="py-24 px-6 md:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-zinc-900/50 border border-neon-lime/50 p-12 backdrop-blur-sm">
              <CheckCircle className="w-20 h-20 text-neon-lime mx-auto mb-6" />
              <h2 className="font-unbounded text-3xl font-bold text-white mb-4">
                Demande envoyée !
              </h2>
              <p className="text-zinc-400 mb-8">
                Merci {formData.name} ! Nous avons bien reçu votre demande et nous vous
                recontacterons sous 24h au{" "}
                <span className="text-neon-lime">{formData.phone}</span>.
              </p>
              <div className="flex justify-center gap-4">
                <a href="tel:0768013271">
                  <Button className="bg-neon-lime text-black font-unbounded font-bold uppercase px-8 py-6">
                    <Phone className="w-5 h-5 mr-2" />
                    Appeler maintenant
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="pt-32 md:pt-40">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-carbon-paper border-b border-zinc-800 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')",
            backgroundSize: "200px",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <p className="font-mono text-neon-lime text-sm tracking-widest uppercase mb-4">
              Contact
            </p>
            <h1
              className="font-unbounded text-4xl md:text-6xl font-black text-white leading-tight mb-6"
              data-testid="contact-title"
            >
              Votre projet
              <br />
              <span className="text-neon-lime text-glow">commence ici</span>
            </h1>
            <p className="font-manrope text-lg md:text-xl text-zinc-400 leading-relaxed">
              Parlez-nous de votre projet d'importation. Plus nous en savons, mieux nous pourrons vous conseiller.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-12 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a
              href="tel:0768013271"
              className="bg-zinc-900/50 border border-zinc-800 p-6 flex items-center gap-4 hover:border-neon-lime/50 transition-colors group"
              data-testid="contact-phone-card"
            >
              <div className="w-14 h-14 bg-neon-lime/10 flex items-center justify-center">
                <Phone className="w-6 h-6 text-neon-lime" />
              </div>
              <div>
                <p className="text-zinc-500 text-sm">Téléphone</p>
                <p className="text-white font-mono text-lg group-hover:text-neon-lime transition-colors">
                  07 68 01 32 71
                </p>
              </div>
            </a>

            <a
              href="mailto:contact@fastandcar.com"
              className="bg-zinc-900/50 border border-zinc-800 p-6 flex items-center gap-4 hover:border-neon-lime/50 transition-colors group"
              data-testid="contact-email-card"
            >
              <div className="w-14 h-14 bg-neon-lime/10 flex items-center justify-center">
                <Mail className="w-6 h-6 text-neon-lime" />
              </div>
              <div>
                <p className="text-zinc-500 text-sm">Email</p>
                <p className="text-white group-hover:text-neon-lime transition-colors">
                  contact@fastandcar.com
                </p>
              </div>
            </a>

            <div className="bg-zinc-900/50 border border-zinc-800 p-6 flex items-center gap-4">
              <div className="w-14 h-14 bg-neon-lime/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-neon-lime" />
              </div>
              <div>
                <p className="text-zinc-500 text-sm">Réponse</p>
                <p className="text-white">Sous 24h</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Multi-Step Form */}
      <section className="py-16 md:py-24 relative">
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')",
            backgroundSize: "200px",
          }}
        />
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-12">
            <div className="flex items-center gap-4">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-unbounded font-bold ${
                      step >= s
                        ? "bg-neon-lime text-black"
                        : "bg-zinc-800 text-zinc-500"
                    }`}
                  >
                    {s}
                  </div>
                  {s < 3 && (
                    <div
                      className={`w-16 h-1 mx-2 ${
                        step > s ? "bg-neon-lime" : "bg-zinc-800"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800 p-8 md:p-12 backdrop-blur-sm">
            <form onSubmit={handleSubmit}>
              {/* Step 1: Contact Info */}
              {step === 1 && (
                <div className="space-y-8" data-testid="step-1">
                  <div className="text-center mb-8">
                    <User className="w-12 h-12 text-neon-lime mx-auto mb-4" />
                    <h2 className="font-unbounded text-2xl font-bold text-white">
                      Vos coordonnées
                    </h2>
                    <p className="text-zinc-400 mt-2">
                      Comment pouvons-nous vous contacter ?
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white">
                        Nom complet *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Jean Dupont"
                        data-testid="input-name"
                        className="bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-600 h-12 rounded-none focus:border-neon-lime"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="jean@example.com"
                        data-testid="input-email"
                        className="bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-600 h-12 rounded-none focus:border-neon-lime"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="phone" className="text-white">
                        Téléphone *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="06 XX XX XX XX"
                        data-testid="input-phone"
                        className="bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-600 h-12 rounded-none focus:border-neon-lime"
                      />
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={() => goToStep(2)}
                    className="w-full bg-neon-lime text-black font-unbounded font-bold uppercase tracking-wider py-6 btn-skew hover:shadow-[0_0_30px_rgba(57,255,20,0.4)]"
                    data-testid="next-step-1"
                  >
                    <span>Continuer</span>
                  </Button>
                </div>
              )}

              {/* Step 2: Qualification Questions */}
              {step === 2 && (
                <div className="space-y-8" data-testid="step-2">
                  <div className="text-center mb-8">
                    <Target className="w-12 h-12 text-neon-lime mx-auto mb-4" />
                    <h2 className="font-unbounded text-2xl font-bold text-white">
                      Votre projet
                    </h2>
                    <p className="text-zinc-400 mt-2">
                      Quelques questions pour mieux vous accompagner
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* Timeline */}
                    <div className="space-y-2">
                      <Label className="text-white flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-neon-lime" />
                        Dans quel délai souhaitez-vous votre véhicule ? *
                      </Label>
                      <Select
                        value={formData.timeline}
                        onValueChange={(value) => handleSelectChange("timeline", value)}
                      >
                        <SelectTrigger
                          data-testid="select-timeline"
                          className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none focus:border-neon-lime"
                        >
                          <SelectValue placeholder="Sélectionnez un délai" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="immédiat">🔥 Le plus tôt possible</SelectItem>
                          <SelectItem value="1-3 mois">📅 Dans 1 à 3 mois</SelectItem>
                          <SelectItem value="3-6 mois">📆 Dans 3 à 6 mois</SelectItem>
                          <SelectItem value="+6 mois">⏳ Plus de 6 mois</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Financing Type */}
                    <div className="space-y-2">
                      <Label className="text-white flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-neon-lime" />
                        Comment comptez-vous financer le véhicule ? *
                      </Label>
                      <Select
                        value={formData.financing_type}
                        onValueChange={(value) => handleSelectChange("financing_type", value)}
                      >
                        <SelectTrigger
                          data-testid="select-financing"
                          className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none focus:border-neon-lime"
                        >
                          <SelectValue placeholder="Type de financement" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="comptant">💵 Comptant (fonds disponibles)</SelectItem>
                          <SelectItem value="credit">🏦 Crédit auto</SelectItem>
                          <SelectItem value="loa">📝 LOA / LLD</SelectItem>
                          <SelectItem value="recherche">🔍 Je recherche un financement</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Checkboxes */}
                    <div className="space-y-4 p-4 bg-zinc-800/30 border border-zinc-700">
                      <p className="text-zinc-400 text-sm mb-4">
                        Pour mieux qualifier votre projet :
                      </p>
                      
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          checked={formData.budget_confirmed}
                          onCheckedChange={(checked) => handleCheckboxChange("budget_confirmed", checked)}
                          data-testid="checkbox-budget"
                          className="border-zinc-600 data-[state=checked]:bg-neon-lime data-[state=checked]:border-neon-lime"
                        />
                        <Label className="text-white text-sm">
                          J'ai défini mon budget précisément
                        </Label>
                      </div>

                      <div className="flex items-center space-x-3">
                        <Checkbox
                          checked={formData.funds_available}
                          onCheckedChange={(checked) => handleCheckboxChange("funds_available", checked)}
                          data-testid="checkbox-funds"
                          className="border-zinc-600 data-[state=checked]:bg-neon-lime data-[state=checked]:border-neon-lime"
                        />
                        <Label className="text-white text-sm">
                          Mes fonds sont disponibles / Mon financement est validé
                        </Label>
                      </div>

                      <div className="flex items-center space-x-3">
                        <Checkbox
                          checked={formData.vehicle_identified}
                          onCheckedChange={(checked) => handleCheckboxChange("vehicle_identified", checked)}
                          data-testid="checkbox-vehicle"
                          className="border-zinc-600 data-[state=checked]:bg-neon-lime data-[state=checked]:border-neon-lime"
                        />
                        <Label className="text-white text-sm">
                          J'ai déjà identifié le véhicule que je recherche
                        </Label>
                      </div>

                      <div className="flex items-center space-x-3">
                        <Checkbox
                          checked={formData.decision_maker}
                          onCheckedChange={(checked) => handleCheckboxChange("decision_maker", checked)}
                          data-testid="checkbox-decision"
                          className="border-zinc-600 data-[state=checked]:bg-neon-lime data-[state=checked]:border-neon-lime"
                        />
                        <Label className="text-white text-sm">
                          Je suis le décisionnaire final pour cet achat
                        </Label>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Button
                      type="button"
                      onClick={() => setStep(1)}
                      variant="outline"
                      className="flex-1 border-zinc-700 text-white font-unbounded uppercase py-6"
                    >
                      Retour
                    </Button>
                    <Button
                      type="button"
                      onClick={() => goToStep(3)}
                      className="flex-1 bg-neon-lime text-black font-unbounded font-bold uppercase tracking-wider py-6 btn-skew hover:shadow-[0_0_30px_rgba(57,255,20,0.4)]"
                      data-testid="next-step-2"
                    >
                      Continuer
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Project Details */}
              {step === 3 && (
                <div className="space-y-8" data-testid="step-3">
                  <div className="text-center mb-8">
                    <Car className="w-12 h-12 text-neon-lime mx-auto mb-4" />
                    <h2 className="font-unbounded text-2xl font-bold text-white">
                      Détails du véhicule
                    </h2>
                    <p className="text-zinc-400 mt-2">
                      Décrivez-nous le véhicule de vos rêves
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-white flex items-center gap-2">
                        <Euro className="w-4 h-4 text-neon-lime" />
                        Budget approximatif
                      </Label>
                      <Select
                        value={formData.budget}
                        onValueChange={(value) => handleSelectChange("budget", value)}
                      >
                        <SelectTrigger
                          data-testid="select-budget"
                          className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none focus:border-neon-lime"
                        >
                          <SelectValue placeholder="Sélectionnez un budget" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="< 15000€">Moins de 15 000€</SelectItem>
                          <SelectItem value="15000€ - 25000€">15 000€ - 25 000€</SelectItem>
                          <SelectItem value="25000€ - 40000€">25 000€ - 40 000€</SelectItem>
                          <SelectItem value="40000€ - 60000€">40 000€ - 60 000€</SelectItem>
                          <SelectItem value="> 60000€">Plus de 60 000€</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white">Provenance souhaitée</Label>
                      <Select
                        value={formData.origin}
                        onValueChange={(value) => handleSelectChange("origin", value)}
                      >
                        <SelectTrigger
                          data-testid="select-origin"
                          className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none focus:border-neon-lime"
                        >
                          <SelectValue placeholder="Europe ou Corée ?" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="Europe">🇪🇺 Europe</SelectItem>
                          <SelectItem value="Corée du Sud">🇰🇷 Corée du Sud</SelectItem>
                          <SelectItem value="Les deux">Les deux / Sans préférence</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white">Type de véhicule recherché</Label>
                      <Input
                        name="vehicle_type"
                        type="text"
                        value={formData.vehicle_type}
                        onChange={handleChange}
                        placeholder="Ex: SUV, Berline, Break, Coupé..."
                        data-testid="input-vehicle-type"
                        className="bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-600 h-12 rounded-none focus:border-neon-lime"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white">Forfait qui vous intéresse</Label>
                      <Select
                        value={formData.pack_interest}
                        onValueChange={(value) => handleSelectChange("pack_interest", value)}
                      >
                        <SelectTrigger
                          data-testid="select-pack"
                          className="bg-zinc-900/50 border-zinc-800 text-white h-12 rounded-none focus:border-neon-lime"
                        >
                          <SelectValue placeholder="Sélectionnez un forfait" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="Pack Essentiel">Pack Essentiel - 990€</SelectItem>
                          <SelectItem value="Pack Avancé">Pack Avancé - 1 290€</SelectItem>
                          <SelectItem value="Pack Sécurité">Pack Sécurité - 2 490€</SelectItem>
                          <SelectItem value="Pack Premium">Pack Premium - 3 990€</SelectItem>
                          <SelectItem value="Import Corée">Import Corée (sur devis)</SelectItem>
                          <SelectItem value="Je ne sais pas encore">Je ne sais pas encore</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white">
                      Décrivez votre projet
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Marque, modèle, année, kilométrage souhaité, options particulières, ou toute autre information utile..."
                      rows={5}
                      data-testid="input-message"
                      className="bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-600 rounded-none focus:border-neon-lime resize-none"
                    />
                  </div>

                  <div className="flex gap-4">
                    <Button
                      type="button"
                      onClick={() => setStep(2)}
                      variant="outline"
                      className="flex-1 border-zinc-700 text-white font-unbounded uppercase py-6"
                    >
                      Retour
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="submit-btn"
                      className="flex-1 bg-neon-lime text-black font-unbounded font-bold uppercase tracking-wider py-6 btn-skew hover:shadow-[0_0_30px_rgba(57,255,20,0.4)] disabled:opacity-50"
                    >
                      <span className="flex items-center gap-2 justify-center">
                        {isSubmitting ? (
                          "Envoi en cours..."
                        ) : (
                          <>
                            Envoyer ma demande
                            <Send className="w-5 h-5" />
                          </>
                        )}
                      </span>
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
